export enum PaymentErrorCodes {
  // TODO (CY) 20241011
  // Some of the edge-case error codes exist in the Monolith's stripe controller (Redbubble repo: app/api/redbubble/api/checkout/stripe.rb):
  // - invalid_address
  //    - This occurs when the shipping country or billing country is invalid. Redbubble repo: app/models/country.rb L253
  //    - The error message is already localised in the backend
  // - payment_error
  //    - I can't find where this error is raised in the Monolith. I can't find it in stripe error codes either ¯\_(ツ)_/¯
  // - licensed_work_not_saleable
  //    - This occurs when the purchase order contains non-saleable licensed work. Redbubble repo: lib/purchase_orders/licensed_work_validator.rb
  //    - The error message is already localised in the backend
  // - no_purchasable_items_error
  //    - This occurs when one or more items in the user's cart is no longer available for purchase. Redubble repo: app/models/purchase_order.rb L236
  //    - The error message is already localised in the backend
  //
  // The error codes above aren't included in the error dictionary because they aren't already included in the
  // Monolith's paymentErrorLookup.js file. These error codes aren't in the error dictionary now because
  // I didn't want to start handling edge-case errors that aren't being handled by the Monolith's Checkout frontend.

  // NOTE (CY): The error codes that have not been ported from the Monolith:
  // invalid_expiry_month -> no need to handle this error because Stripe will check this as the user enters their card details
  // invalid_expiry_year ->  no need to handle this error because Stripe will check this as the user enters their card details
  // invalid_cvc -> no need to handle this error because Stripe will check this as the user enters their card details
  // incorrect_number -> no need to handle this error because Stripe will check this as the user enters their card details
  // form_error -> no need to handle this error because we have a different form strategy
  // incorrect_zip -> no need to handle this error because we will 'wait and see' how often Stripe returns this error code

  // Origin: Zero payment controller
  // Cause:
  // 1. The cart's payable amount that the user is seeing in the frontend doesn't match the
  //    purchase_order.amount_paid_inc_tax_buyer_currency record in the database
  // 2. The cart's line item count that the user is seeing in the frontend doesn't match the
  //    purchase_order.non_postage_items_count record in the database
  CANT_RECONCILE_CART = 'cant_reconcile_cart',

  // Origin: Server-side in Monolith Stripe and PayPal endpoints
  // Causes:
  // 1. A work in the cart is no longer sellable
  LICENSED_WORK_NOT_SALEABLE = 'licensed_work_not_saleable',

  INVALID_PURCHASE_ORDER = 'invalid_purchase_order',
  INVALID_ADDRESS = 'invalid_address',

  // Origin: Server-side in Monolith Stripe and PayPal endpoints
  // Causes:
  // 1. A cart has no sellable items (e.g. expired works, unsellable in country)
  NO_PURCHASABLE_ITEMS = 'no_purchasable_items_error',

  // Origin: stripe.confirmPayment() or elements.submit()
  // Doc: https://docs.stripe.com/error-codes#card-declined
  CARD_DECLINED = 'card_declined',

  // XXX (CY): I'm unable to produce this error, but I'm adding the error code here anyway because
  // stripe lists this error code as part of the declined error code
  // Doc: https://docs.stripe.com/declines/codes
  CARD_NOT_SUPPORTED = 'card_not_supported',

  // Origin: Monolith Stripe PaymentIntent endpoint `/pay`
  // Causes:
  // 1. Sift Science rejects the purchase order
  //
  // Origin: On the redirect from Monolith Stripe Payment Verify endpoint `/payment_intents_verify`
  // Causes:
  // 1. Stripe PaymentIntent's status = 'requires_payment_method' or 'requires_source'
  CHARGE_FAILED = 'charge_failed',

  // Origin: On the redirect from Monolith Stripe Payment Verify endpoint `/payment_intents_verify`
  // Causes:
  // 1. Stripe PaymentIntent's status = 'requires_action'
  // 2. Unexpected Stripe error occurred when retrieving the paymentIntent
  CHARGE_FAILED_RETRY_ALLOWED = 'charge_failed_retry_allowed',

  // Origin: Monolith Stripe PaymentIntent endpoint `/pay`
  // Cause:
  // 1. ActiveRecord error with the user's inputs
  // 2. Janus and authentication error
  // 3. Stripe's InvalidRequestError occurs in production environment
  // 4. Stripe's CardError occurs in production environment
  // 5. Any unknown exception that occurred in the /pay endpoint
  //
  // The defaultMessage is localised from the backend.
  // In prod, defaultMessage = "An error occurred. Please try again later."
  // In non-prod, the defaultMessage is the exception message itself.
  DEFAULT = 'default',

  // Origin: Stripe - during confirmPayment()
  // Doc: https://docs.stripe.com/error-codes#expired-card
  EXPIRED_CARD = 'expired_card',

  // Origin: Stripe - during confirmPayment()
  // Doc: https://docs.stripe.com/error-codes#incorrect-cvc
  INCORRECT_CVC = 'incorrect_cvc',

  // Origin: Monolith Stripe PaymentIntent endpoint `/pay`
  // Causes:
  // 1. The Cart's total doesn't meet the minimum threshold of a payment method.
  //    The threshold can be found in Redbubble repo: lib/purchase_orders/amount_validator.rb
  //
  // defaultMessage = "Purchase order amount (%{amount}) is not within acceptable thresholds."
  // The defaultMessage is localised from the backend and can be found in Redbubble repo: lib/purchase_orders/amount_validator.rb
  INVALID_AMOUNT = 'invalid_amount',

  // Origin: Monolith Stripe PaymentIntent endpoint `/pay`
  // Cause:
  // 1. Coupon expired
  // 2. Coupon not valid for the user's shipping country
  // 3. The Cart's subtotal does not meet the coupon's threshold
  //
  // The defaultMessage is localised from the backend and can be found in Redbubble repo: app/models/coupons/coupon.rb
  INVALID_COUPON = 'invalid_coupon',

  // XXX (CY): I'm unable to produce this error, but I'm adding the error code here anyway because
  // stripe lists this error code as part of the declined error code
  // Doc: https://docs.stripe.com/declines/codes
  INVALID_NUMBER = 'invalid_number',

  // Origin: Stripe - during confirmPayment()
  // Doc: https://docs.stripe.com/error-codes#payment-intent-authentication-failure
  PAYMENT_INTENT_AUTHENTICATION_FAILURE = 'payment_intent_authentication_failure',

  // Origin: Stripe - during confirmPayment()
  // Doc: https://docs.stripe.com/error-codes#processing-error
  PROCESSING_ERROR = 'processing_error',

  // Origin: Frontend's usePay.ts
  // Causes:
  // 1. Unexpected exception occurred in the HTTP request to /pay
  // 2. client_secret is in the success response from /pay
  //
  // Origin: Monolith Stripe Payment Verify endpoint `/payment_intents_verify`
  // Causes:
  // 1. PaymentIntent has an unknown status
  SERVER_ERROR = 'server_error',

  // Origin: On the redirect from Monolith Stripe PaymentIntent endpoint `/pay`
  // Causes:
  // 1. User has inputted an un-shippable country as their shipping country
  //    As of writing, the blocked countries are "CU, IR, KP, SY" which corresponds the Stripe's blocked countries.
  //    The list of countries can be found in Redbubble repo - app/models/country.rb
  //    Stripe's list of blocked countries: https://stripe.com/au/legal/restricted-businesses ("High-risk jurisdictions" section)
  //
  // defaultMessage = "Sorry, we don't deliver to this country. Please select a different shipping country to proceed."
  // The defaultMessage is localised from the backend and can be found in Redbubble repo: lib/payment/errors.rb
  SHIPPING_COUNTRY_BLOCKED = 'shipping_country_blocked',

  // Origin: Monolith Stripe PaymentIntent endpoint `/pay`
  // Causes:
  // 1. When validation fails on the request body of the POST request towards /pay
  //    The validation rules can be found in Redbubble repo: app/api/redbubble/api/checkout/stripe.rb Line83
  VALIDATION_ERROR = 'validation_error',

  // Origin: Client-side inside StripePayButton component - during handlePaymentSubmit()
  // Causes:
  // 1. Stripe Element has invalid inputs (e.g. missing CVC).
  USER_ERROR = 'user_error',

  // Origin: Server-side in Monolith `/paypal` payment endpoint
  // Causes:
  // 1. Sift rejects an order as fraudulent.
  FRAUD_DECLINED = 'fraud_declined',
}

export const isPaymentErrorCode = (a: any): a is PaymentErrorCodes => {
  return Object.values(PaymentErrorCodes).includes(a);
};
